<script lang="ts" setup>
import { computed, ref } from "vue";
import { Cell, CellGroup, Popup, Radio, RadioGroup } from "vant";
import { useAllVisibility } from "@/use/common";
import { useI18n } from "vue-i18n";
import MediaSelectPopup, {
  MediaSelectPopupInstance,
} from "@/components/mobile/media/MediaSelectPopup.vue";
import { IThreadPostItem } from "@/components/mobile/post/ThreadPostItemNew.vue";
import ChatMessageShareSelection from "@/components/mobile/overlay/ChatMessageShareSelection.vue";
import RemindMates from "@/components/mobile/popup/RemindMates.vue";

import { MediaUploadFileItem, ThreadPostInstance } from "@/types/component";
import { MAX_IMG_COUNT } from "@/components/mobile/post/config";
import { StorageSet } from "@/tools/localStorage";
import { useMate } from "@/stores/mate";
import { watchDebounced } from "@vueuse/core";
import { useRoute } from "vue-router";

const route = useRoute();

// 常量
const VISIBILITIES = computed(useAllVisibility);
const DEFAULT_THREAD_POST_ITEM: IThreadPostItem = {
  message: "",
  imgList: [],
  videoList: [],
  sharedMessageList: [],
  visibility: 1,
  mentionMateIdzz: [],
};
// Props
const props = withDefaults(
  defineProps<{
    initialItems?: IThreadPostItem[];
    enableMateSwitch?: boolean;
    enableVisibilitySelection?: boolean;
    enableMultipleMoment?: boolean;
    enableCache?: boolean;
  }>(),
  {
    initialItems: () => [],
    enableMateSwitch: true,
    enableVisibilitySelection: true,
    enableMultipleMoment: true,
    enableCache: false,
  }
);
// 内部状态
const threadItemList = ref<IThreadPostItem[]>(
  props.initialItems.length > 0
    ? props.initialItems
    : [Object.assign({}, DEFAULT_THREAD_POST_ITEM)]
);
if (props.enableCache) {
  const mateStore = useMate();
  const filterUploadedMedia = (mediaList: MediaUploadFileItem[]) => {
    return (
      mediaList
        ?.filter((img) => img.status === "done")
        .map((img) => {
          delete img.content;
          if (!img.visibility) {
            img.visibility = 1;
            img.crystalPrice = 0;
          }
          return img;
        }) ?? []
    );
  };
  watchDebounced(
    () => threadItemList.value,
    (val) => {
      if (route.query.uuid) return;
      const filteredThreadItemList = val.map((item) => {
        const filteredItem = Object.assign({}, item);
        // 去掉File和base64，避免超过localStorage Quota
        filteredItem.imgList = filterUploadedMedia(item.imgList);
        filteredItem.videoList = filterUploadedMedia(item.videoList);
        return filteredItem;
      });
      StorageSet(
        `threadItemList:${mateStore.currentMate?.id}`,
        filteredThreadItemList
      );
    },
    {
      deep: true,
      debounce: 500,
      rejectOnCancel: true,
    }
  );
}

const editingItemIndex = ref<number>(0);
const editingItem = computed<IThreadPostItem>({
  get: () => threadItemList.value[editingItemIndex.value],
  set: (val) => (threadItemList.value[editingItemIndex.value] = val),
});
const visibility = computed({
  get: () => threadItemList.value[0].visibility,
  set: (val) => (threadItemList.value[0].visibility = val),
});
const imgSelectPopupRef = ref<MediaSelectPopupInstance | null>(null);
// popup状态
const { t } = useI18n();
const showVisibilitySelection = ref(false);
const showImgUploadPopup = ref(false);
const showVideoUploadPopup = ref(false);
const showChatSelection = ref(false);
const showRemindMates = ref(false);

// Methods
const switchEditingItem = (idx: number) => {
  if (idx === editingItemIndex.value) return;
  editingItemIndex.value = idx;
};
const addItem = () => {
  // 当前编辑项目后面添加一个新的项目
  const targetIdx = editingItemIndex.value + 1;
  threadItemList.value.splice(targetIdx, 0, {
    ...DEFAULT_THREAD_POST_ITEM,
    visibility,
  });
  editingItemIndex.value = targetIdx;
};

const deleteItem = (idx: number) => {
  if (threadItemList.value.length === 1) return;
  threadItemList.value.splice(idx, 1);
  editingItemIndex.value = Math.min(
    editingItemIndex.value,
    threadItemList.value.length - 1
  );
};

const showGenGallery = () => {
  imgSelectPopupRef.value!.showGenGallery = true;
  showImgUploadPopup.value = true;
};

const onVideoConfirm = (mediaList: MediaUploadFileItem[]) => {
  const storyId = mediaList
    .map((item) => item.storyVideo)
    .filter((item) => item)[0]?.storyId;
  if (storyId) {
    editingItem.value.storyId = storyId;
  }
};

// Expose
defineExpose<ThreadPostInstance>({
  items: threadItemList,
  visibility,
});
// LifeCycle
</script>
<template>
  <div class="h-full flex flex-col gap-8">
    <thread-post-item-new
      v-for="(_, idx) in threadItemList"
      :key="idx"
      v-model:item="threadItemList[idx]"
      :enable-delete="threadItemList.length > 1"
      :enable-visibility-selection="
        props.enableVisibilitySelection && idx === 0
      "
      :is-editing="idx === editingItemIndex"
      :show-add-btn="enableMultipleMoment"
      @add-item="addItem"
      @delete-item="deleteItem(idx)"
      @trigger-edit="switchEditingItem(idx)"
      @trigger-visibility-select="showVisibilitySelection = true"
      @trigger-img-upload="showImgUploadPopup = true"
      @trigger-video-upload="showVideoUploadPopup = true"
      @trigger-chat-share="showChatSelection = true"
      @trigger-selected-mates="showRemindMates = true"
      @show-gen-gallery="showGenGallery"
    />
    <!-- 切换可见性 -->
    <popup
      v-model:show="showVisibilitySelection"
      position="bottom"
      round
      safe-area-inset-bottom
    >
      <div class="h-full overflow-hidden grid grid-rows-[auto_1fr]">
        <div class="navbar bg-base-100 h-top-nav border-b">
          <!-- 左 -->
          <div class="navbar-start h-full"></div>
          <!-- 中 -->
          <div class="navbar-center">
            <div class="font-bold">{{ $t("Moment.setting") }}</div>
          </div>
          <!-- 右 -->
          <div class="navbar-end"></div>
        </div>
        <div class="flex flex-col bg-base-100 pb-4">
          <radio-group v-model="visibility">
            <cell-group :title="t('Visibilities.visibilityTitle')" inset>
              <cell
                v-for="v in VISIBILITIES"
                :key="v.value"
                :title="v.text"
                :label="v.desc"
                clickable
                @click="visibility = v.value"
              >
                <template #right-icon>
                  <Radio :name="v.value" />
                </template>
              </cell>
            </cell-group>
          </radio-group>
        </div>
      </div>
    </popup>
    <!-- 图片选择 -->
    <media-select-popup
      ref="imgSelectPopupRef"
      v-model:media-list="editingItem.imgList"
      v-model:show="showImgUploadPopup"
      :max-count="MAX_IMG_COUNT"
      :upload-type="5"
      media-type="img"
      multiple
    />
    <!-- 视频选择 -->
    <media-select-popup
      v-model:media-list="editingItem.videoList"
      v-model:show="showVideoUploadPopup"
      :max-count="2"
      :multiple="false"
      :upload-type="5"
      media-type="video"
      replace
      @onMediaSelected="onVideoConfirm"
    />

    <!-- 聊天记录分享 -->
    <chat-message-share-selection
      v-model:selected-chat-message-list="editingItem.sharedMessageList"
      v-model:show="showChatSelection"
    />
    <remind-mates
      v-if="false"
      v-model:at-mate-ids="editingItem.mentionMateIdzz"
      v-model:show="showRemindMates"
    />
  </div>
</template>
